import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { CalcCard, CalcField, CalcRow, CalcStat, pct, usd } from "@/components/site/CalcKit";
import { CalculatorScaffold } from "@/components/site/CalculatorScaffold";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { calcLtv } from "@/lib/investor-calcs";
import ltvPortfolioHero from "@/assets/hero/hero-ltv-portfolio-recapitalization.jpg";

const FAQS = [
  {
    q: "How do I calculate LTV?",
    a: "Loan-to-value is the first mortgage balance divided by the property's appraised value or purchase price, whichever the program uses. A $300,000 loan against a $400,000 value is 75% LTV.",
  },
  {
    q: "What is CLTV and why does it matter?",
    a: "Combined loan-to-value adds every lien — first mortgage plus any second lien, HELOC line, or seller carry — and divides by value. Equity products are capped on CLTV, so an existing second can limit what a new lien can advance even when the first-lien LTV looks low.",
  },
  {
    q: "How much equity can I pull out of a rental?",
    a: "Take the program's maximum LTV or CLTV, multiply by value, and subtract the liens that remain. This calculator shows that figure as available proceeds at your target. Actual maximums vary by program, FICO, property type, and occupancy history.",
  },
  {
    q: "Is a HELOC counted at the balance or the full line?",
    a: "Most programs count the full committed line, not the drawn balance, when calculating CLTV — because the borrower can redraw it. Model the line amount if you want the conservative answer.",
  },
  {
    q: "Does an appraisal replace my value estimate?",
    a: "Yes. Any figure here is an assumption until an appraisal or a lender-accepted valuation is completed. Automated estimates are a screening tool, not a valuation.",
  },
  {
    q: "What LTV do DSCR cash-out refinances typically max out at?",
    a: "Cash-out DSCR programs commonly cap leverage lower than a purchase, often in the 65-75% LTV range depending on credit, property type, and DSCR. The exact cap varies by lender and is not fixed, so treat any percentage here as a starting point for shopping programs.",
  },
  {
    q: "Does property type change my maximum LTV?",
    a: "Yes, single-family and warrantable condos generally support higher maximum LTV than 2-4 unit properties, non-warrantable condos, or short-term rentals, which are often capped several points lower. Rural or unique properties can face further reductions.",
  },
  {
    q: "How does DSCR interact with my LTV cap?",
    a: "A lower DSCR often forces a lower maximum LTV even if the credit and property otherwise qualify for higher leverage, since many pricing grids tier maximum LTV by DSCR band. Improving DSCR — through a larger down payment or interest-only structure — can unlock a higher LTV tier.",
  },
  {
    q: "Can I combine a HELOC with a DSCR first mortgage?",
    a: "It is possible on some programs, but the HELOC's full line still counts toward CLTV, which can limit how much the first mortgage is allowed to advance. Run the combined numbers before assuming both liens will fit under the program's CLTV ceiling.",
  },
  {
    q: "What happens if my equity calculation is based on a stale value?",
    a: "An outdated or optimistic value estimate can make available equity look larger than it really is, since actual proceeds are capped against the appraised value the lender orders, not your own estimate. Refresh comps or get a broker price opinion before relying on the number for planning.",
  },
  {
    q: "Is CLTV or LTV used for a rate-and-term refinance?",
    a: "Rate-and-term refinances typically only consider LTV against the new loan amount replacing the existing lien, since there is no cash out to weigh against subordinate liens. CLTV becomes the binding constraint mainly on cash-out refinances or when a second lien remains in place.",
  },
  {
    q: "Why does my usable equity look smaller than my total equity?",
    a: "Total equity is value minus all liens, but usable equity is capped by the program's maximum LTV or CLTV, which is almost always below 100%. The gap between the two is the cushion the lender requires you to leave in the property.",
  },
];

export const Route = createFileRoute("/ltv-equity-calculator")({
  head: () =>
    buildHead({
      title: "LTV & Equity Calculator for Rentals | Simply Approved",
      description:
        "Calculate LTV, CLTV, and usable equity on an investment property. See the maximum loan at your target LTV and how much a new lien could advance.",
      url: "/ltv-equity-calculator",
      keywords:
        "LTV calculator, CLTV calculator, home equity calculator rental property, loan to value calculator investment property, how much equity can I pull out, combined loan to value, second lien CLTV calculator, rental property equity",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "LTV & Equity Calculator", url: "/ltv-equity-calculator" },
      ],
      service: { name: "LTV and Equity Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "LTV & Equity Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/ltv-equity-calculator",
          description: "LTV, CLTV and usable-equity calculator for investment property.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: LtvPage,
});

type Inputs = {
  value: number;
  firstLien: number;
  secondLien: number;
  targetLtvPct: number;
  costBasis: number;
};

const DEFAULT: Inputs = {
  value: 500000,
  firstLien: 285000,
  secondLien: 0,
  targetLtvPct: 75,
  costBasis: 360000,
};

function LtvPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("ltv_equity", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("ltv_equity", String(k), v as never);
  };

  const m = useMemo(
    () => ({
      ...calcLtv(i.value, i.firstLien, i.secondLien, i.targetLtvPct),
      appreciation: i.value - i.costBasis,
    }),
    [i],
  );

  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackCalcEstimateViewed("ltv_equity", {
      ltv: Number((m.ltv * 100).toFixed(1)),
      available: Math.round(m.availableAtTarget),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Property value", value: usd(i.value) },
    { label: "First lien balance", value: usd(i.firstLien) },
    { label: "Second lien / line", value: usd(i.secondLien) },
    { label: "LTV", value: pct(m.ltv, 1) },
    { label: "CLTV", value: pct(m.cltv, 1) },
    { label: "Equity", value: usd(m.equity) },
    { label: `Available at ${i.targetLtvPct}%`, value: usd(m.availableAtTarget) },
  ];

  return (
    <CalculatorScaffold
      calculator="ltv_equity"
      eyebrow="LTV & equity"
      title="LTV and Equity Calculator for Investment Property"
      subtitle="See LTV, CLTV, equity, and how much a new lien could advance at your target leverage. All outputs are illustrative estimates, not an appraisal, approval, or offer of credit."
      shareUrl={shareUrl}
      shareTitle="My property equity position"
      heroImage={{
        src: ltvPortfolioHero,
        alt: "Real estate investors reviewing equity across a rental-property portfolio to plan additional purchases",
      }}
      inputs={
        <>
          <CalcCard title="1. Value & liens">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Property value" value={i.value} onChange={(v) => set("value", v)} prefix="$" hint="Use an appraised or well-supported comp value." />
              <CalcField label="First lien balance" value={i.firstLien} onChange={(v) => set("firstLien", v)} prefix="$" />
              <CalcField label="Second lien / HELOC line" value={i.secondLien} onChange={(v) => set("secondLien", v)} prefix="$" hint="Most programs count the full committed line, not the drawn balance." />
              <CalcField label="Original cost basis" value={i.costBasis} onChange={(v) => set("costBasis", v)} prefix="$" hint="Purchase price plus capital improvements." />
            </div>
          </CalcCard>
          <CalcCard title="2. Target leverage">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Target LTV / CLTV" value={i.targetLtvPct} onChange={(v) => set("targetLtvPct", v)} suffix="%" step={5} hint="Program maximums vary; DSCR cash-out is commonly capped near 75%." />
            </div>
          </CalcCard>
        </>
      }
      results={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CalcStat label="LTV" value={pct(m.ltv, 1)} />
            <CalcStat label="CLTV" value={pct(m.cltv, 1)} colorClass={m.cltv > i.targetLtvPct / 100 ? "text-destructive" : "text-emerald-600"} />
            <CalcStat label="Equity" value={usd(m.equity)} />
            <CalcStat label={`Available at ${i.targetLtvPct}%`} value={usd(m.availableAtTarget)} colorClass={m.availableAtTarget > 0 ? "text-emerald-600" : "text-muted-foreground"} />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <h3 className="font-display text-base font-semibold text-foreground">Equity detail</h3>
            <dl className="mt-3 space-y-1.5 text-sm">
              <CalcRow label="Property value" value={usd(i.value)} />
              <CalcRow label="Total liens" value={`−${usd(i.firstLien + i.secondLien)}`} />
              <CalcRow label="Equity" value={usd(m.equity)} strong />
              <CalcRow label="Equity as % of value" value={pct(m.equityPct, 1)} />
              <CalcRow label={`Max loan at ${i.targetLtvPct}%`} value={usd(m.maxLoanAtTarget)} />
              <CalcRow label="Less existing liens" value={`−${usd(i.firstLien + i.secondLien)}`} />
              <CalcRow label="Indicated proceeds" value={usd(m.availableAtTarget)} strong colorClass={m.availableAtTarget > 0 ? "text-emerald-600" : "text-destructive"} />
              <CalcRow label="Value over cost basis" value={usd(m.appreciation)} colorClass={m.appreciation >= 0 ? "text-emerald-600" : "text-destructive"} />
            </dl>
          </div>
        </div>
      }
      aiFacts={aiFacts}
      aiSuggestions={[
        "What is the difference between LTV and CLTV here?",
        "Why does my HELOC line reduce available proceeds?",
        "Is a second lien or a cash-out refinance the better structure?",
        "What could change my value at appraisal?",
      ]}
      methodologyIntro="Leverage math is simple; the judgment is in the value you use and which liens count."
      methodology={
        <>
          <p>
            <strong>LTV vs CLTV.</strong> LTV uses the first lien only. CLTV adds every recorded lien. Equity products
            are governed by CLTV, which is why a modest second can block a large first-lien cash-out.
          </p>
          <p>
            <strong>Available proceeds.</strong> Target LTV × value − existing liens. That is a gross figure: closing
            costs, prepayment penalties on the loan being retired, and escrow shortages all reduce what reaches you.
          </p>
          <p>
            <strong>Seasoning.</strong> Many programs limit cash-out to the purchase price plus documented improvements
            until the property has been owned for a set period, so a recent purchase may not price off today&apos;s value.
          </p>
        </>
      }
      faqs={FAQS}
      faqTitle="LTV, CLTV and equity FAQ"
      related={[
        { to: "/cash-out-refinance-calculator", label: "Cash-out refinance calculator" },
        { to: "/dscr-heloc-rates", label: "DSCR HELOC pricing" },
        { to: "/dscr-second-lien-heloc", label: "Second-lien DSCR HELOC" },
        { to: "/dscr-cash-out-refinance", label: "DSCR cash-out refinance" },
        { to: "/brrrr-calculator", label: "BRRRR calculator" },
        { to: "/dscr-calculator", label: "DSCR calculator" },
      ]}
      ctaTitle="Turn equity into your next acquisition"
      ctaBody="Share the property and lien detail and a licensed loan officer will outline the equity structures available for business-purpose investment property."
    />
  );
}
